import { apiClient } from "@/lib/api";
import { parseContentDispositionFilename } from "@/lib/contentDisposition";
import type { LabelFormat } from "@/api/routes";
import type {
  BuildRouteMixedPayload,
  BuildRouteMixedResponse,
  BulkDeleteAccepted,
  BulkDeleteOrdersResponse,
  BulkDeleteOrdersResult,
  BulkDeleteTaskStatus,
  CreateOrderPayload,
  GeocodingProgress,
  ImportOrdersResult,
  Order,
  OrderFilterCounts,
  OrderStatus,
  PaginatedOrders,
  PaginatedUnifiedRows,
  Parcel,
  ParcelWritePayload,
  TransitionOrderPayload,
  UpdateOrderPayload,
} from "@/types/orders";
import type {
  SavedFilter,
  SavedFilterCreatePayload,
  SavedFilterUpdatePayload,
} from "@/types/savedFilters";

export interface GetOrdersParams {
  page?: number;
  page_size?: number;
  /**
   * Order status filter. Accepts a single value or an array — axios serializes
   * an array as repeated `?status=` params, which the Orders `BaseInFilter`
   * (TASK-4432) reads via `getlist` and returns as the union of the requested
   * statuses. An empty/omitted value means "all statuses".
   */
  status?: string[] | string;
  delivery_type?: "delivery" | "pickup" | "";
  delivery_date?: string;
  delivery_date__gte?: string;
  delivery_date__lte?: string;
  search?: string;
  ordering?: string;
  /**
   * BUG-410: `true` = only orders still awaiting asynchronous geocoding
   * (`lat`/`lon` null and not marked failed); `false` = the complement.
   * Useful for listing problem addresses. NOT the progress-banner source —
   * that is {@link getGeocodingProgress}, which is scoped to one import batch
   * rather than the whole tenant.
   */
  geocode_pending?: boolean;
  /** BUG-410: `true` = only orders whose address could not be geocoded. */
  geocode_failed?: boolean;
  /**
   * Sprint 228 — `true` = only rows whose locality is `ambiguous`, `not_found`,
   * or not resolved yet while the row HAS coordinates. Applies to both halves of
   * the unified list. Omit for "no filter" (never send `false`).
   */
  locality_problem?: boolean;
  /**
   * TASK-4832 — only the orders one permanent order (`OrderTemplate`) produced.
   *
   * `Order.source_template` is set exclusively by the build-from-templates flow,
   * so this returns exactly what the template has actually materialized;
   * hand-created and imported orders carry a NULL FK and never appear.
   *
   * Archived orders are EXCLUDED, because the backend's `get_queryset` already
   * drops them from `list` unless `?status=archived` is passed and this filter
   * does not touch that rule. That is deliberate (owner ruling): the count the
   * dispatcher reads must equal the rows they would see after clicking through
   * to the list with the same filter. Do NOT top the count up with a second
   * archived request.
   *
   * The «N заказов» figure is the envelope's `count` — a database COUNT(*) over
   * the filtered queryset — NEVER `results.length` (the BUG-274..278 class).
   */
  source_template?: number;
  /**
   * `Order.order_type` — `"recurring"` narrows the list to permanent orders,
   * which is exactly the set `POST /route-templates/{id}/stops/` accepts
   * (`routes/views.py:2463` rejects anything else).
   *
   * A real, validated backend filter: an unknown value returns 400
   * («Select a valid choice»), not a silently unfiltered list. Note
   * `"one_time"` is accepted but does NOT narrow — measured on dev it returns
   * the full order count — so only `"recurring"` is useful here.
   */
  order_type?: "recurring" | "one_time";
  /**
   * TASK-5058 — tag filter, repeatable.
   *
   * An array is serialized by axios as repeated `?tags=` params, which the backend reads
   * via `getlist`. The repeated form is the contract rather than a comma-joined string
   * because a tag may legitimately contain a comma.
   *
   * Multi-tag semantics are the SAME as the route-template picker's: AND by default, OR
   * with {@link tags_match}. A single value behaves exactly as it always did.
   *
   * Applies to BOTH halves of the unified list — `Order` and `OrderTemplate` each carry a
   * `tags` column — so the count and the rows agree under it.
   */
  tags?: string[] | string;
  /** TASK-5058 — `all` (default, AND) or `any` (OR). Unknown values degrade to `all`. */
  tags_match?: "all" | "any";
  /**
   * TASK-5058 — the orders ONE import call created, by the REAL batch id (repeatable).
   *
   * `Order.import_batch_id` is a uuid4 stamped per import, so two files uploaded on the
   * same day are two distinct filters — something a creation-date grouping cannot express.
   * Options come from {@link getOrderImportBatches}.
   *
   * On the unified list this removes EVERY permanent-order row: `import_batch_id` is an
   * `Order`-only column, so the predicate is false for a template (the same rule as
   * `source_template`, not the delivery-date rule).
   */
  import_batch?: string[] | string;
  /**
   * TASK-5058 — the tenant-local CREATION day, for "imported today" when the dispatcher
   * does not know which file. Resolved in the tenant's timezone on the server, never in
   * UTC, so the boundary is the tenant's midnight. Applies to both halves of the unified
   * list (`created_at` is on every row).
   */
  created_at__date?: string;
  created_at__gte?: string;
  created_at__lte?: string;
}

/**
 * Fetches a paginated list of orders.
 * GET /api/v1/orders/
 */
export async function getOrders(
  params?: GetOrdersParams,
): Promise<PaginatedOrders> {
  const response = await apiClient.get<PaginatedOrders>("/v1/orders/", {
    params,
  });
  return response.data;
}

/**
 * Query params of the unified Orders list (TASK-4702). A superset of the plain
 * order filters: the order-only filters narrow the ORDER side and are ignored
 * (not rejected) on the template side.
 */
export interface GetOrdersUnifiedParams extends GetOrdersParams {
  /**
   * Source selector. `all` (both kinds) is the backend default when the param is
   * absent; an unknown value degrades to `all`. Owned by TASK-4705's type filter
   * control — passed through here so the seam already exists.
   */
  type?: "all" | "orders" | "templates";
}

/**
 * Fetches ONE paginated list containing regular orders AND permanent orders.
 * GET /api/v1/orders/unified/
 *
 * Every row carries a `kind` discriminator and a composite `row_id`
 * (`"order:12"` / `"template:12"`). The numeric `id` collides across the two
 * sources, so callers MUST key selection on `row_id`.
 *
 * Two behaviours are deliberate product decisions, not bugs:
 *   - a `delivery_date__gte/lte` filter narrows the ORDER side only, so template
 *     rows stay visible under any date filter (a dispatcher filtered to one day
 *     can still add permanent orders to that day's route);
 *   - a `status` filter excludes every template row, since a permanent order has
 *     no lifecycle status to match.
 *
 * `status_counts` and `sums` are whole-list aggregates computed before
 * pagination, so they are identical on every page.
 */
export async function getOrdersUnified(
  params?: GetOrdersUnifiedParams,
): Promise<PaginatedUnifiedRows> {
  const response = await apiClient.get<PaginatedUnifiedRows>(
    "/v1/orders/unified/",
    { params },
  );
  return response.data;
}

/**
 * Query params of `GET /api/v1/orders/filter-counts/` (TASK-4823).
 *
 * A COUNT-ONLY subset of the list params: `ordering`, `page` and `page_size`
 * are deliberately absent — they cannot change how many rows match, so sending
 * them would only fragment the cache key for identical answers.
 *
 * `type` IS sent (BUG-505): the endpoint used to count `Order` rows only and
 * ignore `type`, which made it disagree with `/unified/` — measured on dev,
 * `type=templates` listed 14 rows while the counts said 5 920.
 */
export interface GetOrdersFilterCountsParams {
  /** Array → repeated `?status=` params, matching the list endpoint. */
  status?: string[] | string;
  delivery_type?: "delivery" | "pickup" | "";
  delivery_date__gte?: string;
  delivery_date__lte?: string;
  search?: string;
  type?: "all" | "orders" | "templates";
  /**
   * TASK-5058. These MUST be sent whenever the list sends them: `count` here is
   * «Показать N», defined as the number of rows pressing it will show. A filter the
   * counter does not know about makes it overstate by exactly the rows that filter
   * removes — the BUG-505/BUG-515 class, where «найдено 1520 из 2375» sat above 5 rows.
   */
  tags?: string[] | string;
  tags_match?: "all" | "any";
  import_batch?: string[] | string;
  created_at__date?: string;
  created_at__gte?: string;
  created_at__lte?: string;
  /** Sprint 228 — same predicate as {@link GetOrdersParams.locality_problem}. */
  locality_problem?: boolean;
}

/**
 * Counts the rows a filter set would return, WITHOUT fetching them.
 * GET /api/v1/orders/filter-counts/
 *
 * Drives the filters panel's «найдено N из M», the per-status chip counts and
 * the «Показать N» commit button — all describing a PENDING set the dispatcher
 * has not applied yet, which is why a count-only endpoint exists at all.
 *
 * These figures are server-computed over the whole filtered queryset. Never
 * substitute a tally of the loaded page: that page-local lie reports at most
 * `page_size` and changes as the dispatcher pages, which is exactly the defect
 * BUG-274..278 fixed elsewhere on the dispatcher.
 */
export async function getOrdersFilterCounts(
  params?: GetOrdersFilterCountsParams,
): Promise<OrderFilterCounts> {
  const response = await apiClient.get<OrderFilterCounts>(
    "/v1/orders/filter-counts/",
    { params },
  );
  return response.data;
}

/** One entry of `GET /api/v1/orders/tags/` (TASK-5058). */
export interface OrderTagOption {
  tag: string;
  count: number;
}

/**
 * Distinct tags on the tenant's orders AND permanent orders, with row counts.
 * GET /api/v1/orders/tags/
 *
 * Counts BOTH unified sources, narrowed by the same filters the list is under, so an
 * option's `count` equals the number of rows selecting it opens. Ordered by `count`
 * descending then `tag` ascending — stable across calls.
 *
 * Not paginated by design: paginating a filter's option list silently drops options.
 */
export async function getOrderTags(
  params?: Pick<
    GetOrdersUnifiedParams,
    | "type"
    | "status"
    | "delivery_type"
    | "delivery_date__gte"
    | "delivery_date__lte"
    | "search"
  >,
): Promise<{ results: OrderTagOption[] }> {
  const response = await apiClient.get<{ results: OrderTagOption[] }>(
    "/v1/orders/tags/",
    { params },
  );
  return response.data;
}

/**
 * One entry of `GET /api/v1/orders/import_batches/` (TASK-5058).
 *
 * TWO kinds, and `kind` decides which param the entry is fed back through — never
 * guess from whether `batch` is null:
 *  - `"batch"` — one uploaded file, by the real `Order.import_batch_id`. Send
 *    `?import_batch=<batch>`.
 *  - `"day"` — a tenant-local creation day, over orders carrying NO batch id. `batch`
 *    is `null`. Send `?created_at__date=<date>`.
 *
 * Both exist because neither alone works: measured on dev, only 20 of 15 850 orders
 * carry a batch id (0 on the largest tenants) — only the file importer stamps one, while
 * seeds, the API and the public API do not — so a batch-only list is empty for almost
 * every tenant; while a day-only list would merge the same-day uploads that DO exist.
 */
export interface OrderImportBatchOption {
  /** The real `Order.import_batch_id`, or `null` on a `"day"` entry. */
  batch: string | null;
  /** Tenant-local day: the batch's newest `created_at`, or the day group itself. */
  date: string;
  count: number;
  kind: "batch" | "day";
}

/**
 * The tenant's imported orders grouped by the REAL import batch id, newest first.
 * GET /api/v1/orders/import_batches/
 *
 * Real batches first (`kind: "batch"`, grouped by `Order.import_batch_id`, so two files
 * imported on the same day are two entries — which the permanent-order picker's own
 * `import_batches` endpoint cannot do, `OrderTemplate` having no such column), then
 * tenant-local creation days (`kind: "day"`) over the orders that carry no batch id.
 *
 * Capped at 30 entries total across both kinds; `truncated` says whether older ones were
 * cut, so the UI can say so rather than presenting a partial list as complete.
 */
export async function getOrderImportBatches(
  params?: Pick<
    GetOrdersUnifiedParams,
    | "status"
    | "delivery_type"
    | "delivery_date__gte"
    | "delivery_date__lte"
    | "search"
  >,
): Promise<{ results: OrderImportBatchOption[]; truncated: boolean }> {
  const response = await apiClient.get<{
    results: OrderImportBatchOption[];
    truncated: boolean;
  }>("/v1/orders/import_batches/", { params });
  return response.data;
}

// ---------------------------------------------------------------------------
// Saved filters (TASK-4824)
//
// The whole saved-filter surface is isolated in these four functions so wiring
// it to the real backend is a ONE-FILE change. The panel and its tests talk to
// this module, never to axios.
// ---------------------------------------------------------------------------

/**
 * Lists the current user's saved filters (≤ 5).
 * GET /api/v1/orders/saved-filters/
 *
 * Tolerates BOTH shapes the backend might ship: a DRF-paginated envelope
 * (`{results: [...]}`) and a bare array. Five rows never need pagination, but
 * the default DRF pagination class applies itself unless a view opts out, and
 * guessing wrong here would render an empty section with no error.
 */
export async function getSavedFilters(): Promise<SavedFilter[]> {
  const response = await apiClient.get<
    SavedFilter[] | { results: SavedFilter[] }
  >("/v1/orders/saved-filters/");
  const data = response.data;
  if (Array.isArray(data)) return data;
  return data?.results ?? [];
}

/**
 * Saves a new filter under a name.
 * POST /api/v1/orders/saved-filters/ → 201
 *
 * The 6th save is rejected by the server with `code:
 * "SAVED_FILTER_LIMIT_REACHED"`. Callers branch on that CODE, never on the
 * prose (DEV_RULES §6.4 — `code` is a contract, `detail` is copy).
 */
export async function createSavedFilter(
  payload: SavedFilterCreatePayload,
): Promise<SavedFilter> {
  const response = await apiClient.post<SavedFilter>(
    "/v1/orders/saved-filters/",
    payload,
  );
  return response.data;
}

/**
 * Renames a saved filter and/or overwrites its conditions.
 * PATCH /api/v1/orders/saved-filters/{id}/
 *
 * Also the "replace one" path at the 5-filter limit: replacement is an explicit
 * client-driven overwrite of a row the user chose, never a silent server-side
 * eviction of the oldest.
 */
export async function updateSavedFilter(
  id: number,
  payload: SavedFilterUpdatePayload,
): Promise<SavedFilter> {
  const response = await apiClient.patch<SavedFilter>(
    `/v1/orders/saved-filters/${id}/`,
    payload,
  );
  return response.data;
}

/**
 * Deletes a saved filter.
 * DELETE /api/v1/orders/saved-filters/{id}/ → 204
 *
 * Deleting a shortcut must not change what the dispatcher is looking at — the
 * caller keeps the current conditions and only drops the highlight.
 */
export async function deleteSavedFilter(id: number): Promise<void> {
  await apiClient.delete(`/v1/orders/saved-filters/${id}/`);
}

/**
 * Builds ONE route from a mixed selection of regular orders and permanent
 * orders in a single call.
 * POST /api/v1/orders/build_route_mixed/ → HTTP 202
 *
 * The backend materializes the selected permanent orders into real one-time
 * orders for `date`, combines them with the selected regular orders, and
 * enqueues a single async build. Either id list may be empty (orders-only
 * behaves exactly like `POST /v1/routes/build/`, templates-only exactly like
 * `POST /v1/order-templates/build_route/`); both empty → 400.
 *
 * `task_id`/`status` match the shared 202 envelope, so the existing build
 * poller works untouched. `created_order_ids` reports the orders freshly
 * materialized by this call — every selected permanent order always yields a
 * NEW order (BUG-439): the same permanent order may be used several times a day
 * and appear in different routes, and each one is a genuine delivery.
 */
export async function buildRouteMixed(
  payload: BuildRouteMixedPayload,
): Promise<BuildRouteMixedResponse> {
  const response = await apiClient.post<BuildRouteMixedResponse>(
    "/v1/orders/build_route_mixed/",
    payload,
  );
  return response.data;
}

/**
 * Fetches a single order by ID.
 * GET /api/v1/orders/{id}/
 */
export async function getOrder(id: number): Promise<Order> {
  const response = await apiClient.get<Order>(`/v1/orders/${id}/`);
  return response.data;
}

/**
 * Creates a new order.
 * POST /api/v1/orders/
 */
export async function createOrder(payload: CreateOrderPayload): Promise<Order> {
  const response = await apiClient.post<Order>("/v1/orders/", payload);
  return response.data;
}

/**
 * Partially updates an existing order.
 * PATCH /api/v1/orders/{id}/
 */
export async function updateOrder(
  id: number,
  payload: UpdateOrderPayload,
): Promise<Order> {
  const response = await apiClient.patch<Order>(`/v1/orders/${id}/`, payload);
  return response.data;
}

/**
 * Deletes an order by ID.
 * DELETE /api/v1/orders/{id}/
 */
export async function deleteOrder(id: number): Promise<void> {
  await apiClient.delete(`/v1/orders/${id}/`);
}

/**
 * Archives a whole selection of orders in ONE request (TASK-4982 / BUG-577).
 * POST /api/v1/orders/bulk-delete/ — body `{ ids: [...] }`.
 *
 * This replaces the per-id `DELETE` fan-out that took prod down: 100 parallel
 * DELETEs meant 100 ASGI threads and 100 Postgres connections against a pool of
 * 96, so a routine dispatcher action returned 500s for every tenant. One request
 * costs one thread and one connection regardless of N.
 *
 * Two response shapes, discriminated on the HTTP status the server chose:
 *   * 200 -> `{ status: "completed", archived, skipped, failed }` — done.
 *   * 202 -> `{ status: "accepted", task_id, accepted }` — the batch exceeded
 *     the backend's synchronous threshold and runs as a Celery task; NOTHING is
 *     archived yet and the caller must poll `bulkDeleteStatus(task_id)`.
 * The `status` discriminant is what stops a caller reading `archived` off a 202.
 */
export async function bulkDeleteOrders(
  ids: number[],
): Promise<BulkDeleteOrdersResponse> {
  const response = await apiClient.post<
    BulkDeleteOrdersResult | BulkDeleteAccepted
  >(`/v1/orders/bulk-delete/`, { ids });

  if (response.status === 202) {
    return { status: "accepted", ...(response.data as BulkDeleteAccepted) };
  }
  return { status: "completed", ...(response.data as BulkDeleteOrdersResult) };
}

/**
 * Polls an enqueued bulk delete (the 202 branch of `bulkDeleteOrders`).
 * GET /api/v1/orders/bulk-delete/{task_id}/status/
 *
 * `result` carries the same `{archived, skipped, failed}` shape as the 200 path
 * once `state` is `"SUCCESS"`, and is `null` in every other state — so one
 * summary surface renders both paths.
 */
export async function bulkDeleteStatus(
  taskId: string,
): Promise<BulkDeleteTaskStatus> {
  const response = await apiClient.get<BulkDeleteTaskStatus>(
    `/v1/orders/bulk-delete/${taskId}/status/`,
  );
  return response.data;
}

/**
 * Triggers a status transition on an order by posting the TARGET status.
 * POST /api/v1/orders/{id}/transition/ — body `{ status: "<target>" }`.
 *
 * The endpoint validates the target against the backend order state machine
 * (`OrderStatusMachine.TRANSITIONS`) and returns HTTP 400 for an illegal
 * transition. It is NOT a named-transition verb API.
 */
export async function transitionOrderStatus(
  id: number,
  status: OrderStatus,
): Promise<Order> {
  const payload: TransitionOrderPayload = { status };
  const response = await apiClient.post<Order>(
    `/v1/orders/${id}/transition/`,
    payload,
  );
  return response.data;
}

/**
 * Bulk-imports orders from a CSV or XLSX file.
 * POST /api/v1/orders/import/
 * Content-Type is set automatically by axios when using FormData.
 */
export async function importOrders(file: File): Promise<ImportOrdersResult> {
  const form = new FormData();
  form.append("file", file);
  const response = await apiClient.post<ImportOrdersResult>(
    "/v1/orders/import/",
    form,
  );
  return response.data;
}

/**
 * Reads the geocoding progress of ONE import batch (BUG-410).
 * GET /api/v1/orders/geocoding-progress/?batch=<uuid>
 *
 * `batch` is the `import_batch_id` returned by {@link importOrders}. Counts are
 * a single tenant-scoped aggregate over that batch's orders — never a
 * tenant-wide "orders without coordinates" count, which would also pick up
 * hand-created orders, public-API orders and a concurrent import's leftovers.
 */
export async function getGeocodingProgress(
  batchId: string,
): Promise<GeocodingProgress> {
  const response = await apiClient.get<GeocodingProgress>(
    "/v1/orders/geocoding-progress/",
    { params: { batch: batchId } },
  );
  return response.data;
}

/**
 * Exports orders as a file (CSV or XLSX).
 * GET /api/v1/orders/export/
 */
export async function exportOrders(params?: {
  format?: "csv" | "xlsx";
  status?: string;
  delivery_date?: string;
  delivery_date__gte?: string;
  delivery_date__lte?: string;
  search?: string;
}): Promise<Blob> {
  const response = await apiClient.get("/v1/orders/export/", {
    params,
    responseType: "blob",
  });
  return response.data as Blob;
}

/**
 * Downloads the bulk import template file.
 * GET /api/v1/orders/import/template/
 */
export async function downloadImportTemplate(): Promise<Blob> {
  const response = await apiClient.get("/v1/orders/import/template/", {
    responseType: "blob",
  });
  return response.data as Blob;
}

/**
 * Returns the count of orders with status "new".
 * GET /api/v1/orders/?status=new&page=1&page_size=1
 */
export async function getNewOrdersCount(): Promise<{ count: number }> {
  const response = await apiClient.get<PaginatedOrders>("/v1/orders/", {
    params: { status: "new", page: 1, page_size: 1 },
  });
  return { count: response.data.count };
}

/** Response of the dispatcher tracking-link read endpoint. */
export interface OrderTrackingLink {
  available: boolean;
  tracking_url: string | null;
  token: string | null;
}

/** Response of the dispatcher resend-tracking-link endpoint. */
export interface ResendTrackingLinkResult {
  sent: boolean;
  channels: string[];
}

/**
 * Fetches the public tracking link for an order (dispatcher/admin only).
 * GET /api/v1/orders/{id}/tracking-link/
 * Returns `{ available: false, ... }` (HTTP 200) when the order has no token yet.
 */
export async function getOrderTrackingLink(
  id: number,
): Promise<OrderTrackingLink> {
  const response = await apiClient.get<OrderTrackingLink>(
    `/v1/orders/${id}/tracking-link/`,
  );
  return response.data;
}

/**
 * Re-sends the tracking link to the customer over the available channels.
 * POST /api/v1/orders/{id}/resend-tracking-link/
 */
export async function resendOrderTrackingLink(
  id: number,
): Promise<ResendTrackingLinkResult> {
  const response = await apiClient.post<ResendTrackingLinkResult>(
    `/v1/orders/${id}/resend-tracking-link/`,
  );
  return response.data;
}

/**
 * Returns the count of unassigned orders within an optional date range.
 * GET /api/v1/orders/unassigned-count/
 */
export async function getUnassignedOrderCount(params: {
  delivery_date__gte?: string;
  delivery_date__lte?: string;
}): Promise<{ count: number }> {
  const { data } = await apiClient.get<{ count: number }>(
    "/v1/orders/unassigned-count/",
    { params },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Parcels (nested under an order) — TASK-3801 write API
// ---------------------------------------------------------------------------

/** Paginated shape returned by the nested parcels list endpoint. */
interface PaginatedParcels {
  count: number;
  next: string | null;
  previous: string | null;
  results: Parcel[];
}

/**
 * Lists an order's parcels.
 * GET /api/v1/orders/{orderId}/parcels/
 * The endpoint is paginated (DRF PageNumberPagination); the full list for a
 * single order fits in one page, so the `results` array is returned directly.
 */
export async function getOrderParcels(orderId: number): Promise<Parcel[]> {
  const response = await apiClient.get<PaginatedParcels>(
    `/v1/orders/${orderId}/parcels/`,
    { params: { page_size: 200 } },
  );
  return response.data.results;
}

/**
 * Creates a parcel on an order.
 * POST /api/v1/orders/{orderId}/parcels/
 */
export async function createOrderParcel(
  orderId: number,
  payload: ParcelWritePayload,
): Promise<Parcel> {
  const response = await apiClient.post<Parcel>(
    `/v1/orders/${orderId}/parcels/`,
    payload,
  );
  return response.data;
}

/**
 * Updates a parcel on an order.
 * PATCH /api/v1/orders/{orderId}/parcels/{parcelId}/
 */
export async function updateOrderParcel(
  orderId: number,
  parcelId: number,
  payload: ParcelWritePayload,
): Promise<Parcel> {
  const response = await apiClient.patch<Parcel>(
    `/v1/orders/${orderId}/parcels/${parcelId}/`,
    payload,
  );
  return response.data;
}

/**
 * Deletes a parcel from an order.
 * DELETE /api/v1/orders/{orderId}/parcels/{parcelId}/
 */
export async function deleteOrderParcel(
  orderId: number,
  parcelId: number,
): Promise<void> {
  await apiClient.delete(`/v1/orders/${orderId}/parcels/${parcelId}/`);
}

/**
 * Downloads the printable parcel-label PDF for the coded parcels of the given
 * orders.
 * POST /api/v1/orders/labels/ — body { order_ids: [...], format: "a4"|"thermal" }
 *
 * Returns the PDF blob together with the filename suggested by the
 * `Content-Disposition` header (falls back to `order-labels-<format>.pdf`).
 * Surfaces backend errors verbatim to the caller (409 when `parcels_enabled` is
 * false, 404 cross-tenant, 403 driver), which must be handled with a toast.
 */
export async function getOrdersLabelsPdf(
  orderIds: number[],
  format: LabelFormat,
): Promise<{ blob: Blob; filename: string }> {
  const response = await apiClient.post<Blob>(
    "/v1/orders/labels/",
    { order_ids: orderIds, format },
    { responseType: "blob" },
  );
  const disposition = response.headers["content-disposition"];
  const filename =
    parseContentDispositionFilename(
      typeof disposition === "string" ? disposition : undefined,
    ) ?? `order-labels-${format}.pdf`;
  return { blob: response.data, filename };
}
